import React, { useEffect, useState } from "react";
import {
  Divider,
  Flex,
  NumberInput,
  Paper,
  SegmentedControl,
  Space,
  Title,
} from "@mantine/core";
import HallPartsSelector from "./HallPartsSelector";
import { SectorProperties } from "../../constants/interface";
import RecalculateSeatsButton from "./options/reCalculateSeats";
import SectorRotation from "./options/sectorRotation";
import SemiCircleAdjustment from "./options/SemiCircleAdjustment";
import TransformSectorInCircle from "./options/TransformSectorInCircle";

interface SetSectorProps {
  px?: string;
  selectionSectorType: string;
  undoStack: any[];
  setUndoStack: React.Dispatch<React.SetStateAction<any[]>>;
  selectedDots: Record<string, any>;
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  sectorProperties: SectorProperties;
  setSectorProperties: React.Dispatch<React.SetStateAction<SectorProperties>>;
  t: (key: string) => string;
}

const SetSector: React.FC<SetSectorProps> = ({
  px,
  selectionSectorType,
  undoStack,
  setUndoStack,
  selectedDots,
  data,
  setData,
  sectorProperties,
  setSectorProperties,
  t,
}) => {
  const [selectedHallPart, setSelectedHallPart] = useState<string | null>(null);
  const [stepX, setStepX] = useState<number>(25);
  const [stepY, setStepY] = useState<number>(25);
  const [sectorRows, setSectorRows] = useState<number>(0);
  const [sectorCols, setSectorCols] = useState<number>(0);

  const directionLeftRightOptions = {
    LeftToRight: t(
      "scheme.redactor.panelOptions.setSector.directionOptions.leftToRight"
    ),
    RightToLeft: t(
      "scheme.redactor.panelOptions.setSector.directionOptions.rightToLeft"
    ),
  };
  const [directionLeftRight, setDirectionLeftRight] = useState<string>(
    Object.keys(directionLeftRightOptions)[0]
  );
  const directionUpDownOptions = {
    UpToDown: t(
      "scheme.redactor.panelOptions.setSector.directionOptions.UpToDown"
    ),
    DownToUp: t(
      "scheme.redactor.panelOptions.setSector.directionOptions.DownToUp"
    ),
  };
  const [directionUpDown, setDirectionUpDown] = useState<string>(
    Object.keys(directionUpDownOptions)[0]
  );

  useEffect(() => {
    setSectorProperties((prev) => ({
      ...prev,
      nameParter: selectedHallPart || "",
      rows: sectorRows,
      cols: sectorCols,
      stepX: stepX,
      stepY: stepY,
      sectorOrientationLeftRight: directionLeftRight,
      sectorOrientationUpDown: directionUpDown,
    }));
  }, [
    stepX,
    stepY,
    sectorRows,
    sectorCols,
    directionLeftRight,
    setSectorProperties,
    selectedHallPart,
    directionUpDown,
  ]);

  return (
    <>
      {selectedDots && Object.keys(selectedDots).length !== 0 ? (
        <>
          <Divider my={15} />

          <Title px={px} order={4}>
            {t("scheme.redactor.panelOptions.setSector.actions.title")}
          </Title>
          <Space h="xs" />
          <Flex px={px} gap={10}>
            <RecalculateSeatsButton
              t={t}
              data={data}
              setData={setData}
              undoStack={undoStack}
              setUndoStack={setUndoStack}
              selectedDots={selectedDots}
            />
          </Flex>

          <Space h="md" />

          <TransformSectorInCircle
            px={px}
            t={t}
            data={data}
            setData={setData}
            setUndoStack={setUndoStack}
            selectedDots={selectedDots}
          />

          <Space h="md" />

          <SemiCircleAdjustment
            px={px}
            t={t}
            data={data}
            setData={setData}
            selectedDots={selectedDots}
          />

          <Space h="md" />
          <Divider variant="dashed" />
          <Space h="sm" />

          <SectorRotation
            px={px}
            t={t}
            data={data}
            setData={setData}
            selectedDots={selectedDots}
            selectionSectorType={selectionSectorType}
          />
        </>
      ) : null}

      <Divider my={15} />

      <Paper px={px}>
        {!sectorProperties.readonly && (
          <>
            <Title mb={5} order={5}>
              {t("scheme.redactor.panelOptions.setSector.title")}
            </Title>

            <SegmentedControl
              w={"100%"}
              value={directionLeftRight}
              onChange={setDirectionLeftRight}
              data={Object.entries(directionLeftRightOptions).map(
                ([value, label]) => ({
                  value,
                  label,
                })
              )}
              style={{
                borderRadius: 0,
              }}
            />
            <SegmentedControl
              w={"100%"}
              value={directionUpDown}
              onChange={setDirectionUpDown}
              data={Object.entries(directionUpDownOptions).map(
                ([value, label]) => ({
                  value,
                  label,
                })
              )}
              style={{
                borderRadius: 0,
              }}
            />
            <Space h="sm" />

            <HallPartsSelector
              selectedHallPart={selectedHallPart!}
              onChange={(value) => setSelectedHallPart(value)}
              t={t}
              readOnly={sectorProperties.readonly}
            />

            <Space h="md" />
            <Flex gap={10}>
              <NumberInput
                min={10}
                label={t("scheme.redactor.panelOptions.setSector.stepsX")}
                value={stepX}
                onChange={(value) => setStepX(Number(value) || 10)}
                readOnly={sectorProperties.readonly}
              />

              <NumberInput
                min={10}
                label={t("scheme.redactor.panelOptions.setSector.stepsY")}
                value={stepY}
                onChange={(value) => setStepY(Number(value) || 10)}
              />
            </Flex>

            <Space h="xs" />
            <Flex gap={10}>
              <NumberInput
                min={1}
                label={t("scheme.redactor.panelOptions.setSector.sectorRows")}
                value={sectorRows}
                onChange={(value) => setSectorRows(Number(value) || 2)}
              />

              <NumberInput
                min={1}
                label={t(
                  "scheme.redactor.panelOptions.setSector.sectorColumns"
                )}
                value={sectorCols}
                onChange={(value) => setSectorCols(Number(value) || 2)}
              />
            </Flex>
          </>
        )}
      </Paper>
    </>
  );
};

export default React.memo(SetSector);
